import { HanamiElement, createShadow, define } from '../core/base';
import { t } from '../core/strings';
import { getResolvedTheme, getTheme, onThemeChange, setTheme, type ThemeMode } from '../core/theme';

const css = `:host{display:inline-flex}button{position:relative;display:grid;place-items:center;width:var(--hn-theme-toggle-size,2.5rem);height:var(--hn-theme-toggle-size,2.5rem);padding:0;overflow:hidden;border:1px solid var(--hn-color-border);border-radius:var(--hn-radius-full);background:var(--hn-color-surface);color:var(--hn-color-text-muted);cursor:pointer;transition:background-color var(--hn-duration-fast),color var(--hn-duration-fast),border-color var(--hn-duration-fast),box-shadow var(--hn-duration-normal)}button:hover{border-color:var(--hn-color-primary-border);color:var(--hn-color-primary-text);box-shadow:var(--hn-shadow-glow)}button:focus-visible{outline:none;box-shadow:var(--hn-focus-ring)}svg{grid-area:1 / 1;width:1.25rem;height:1.25rem;transition:transform var(--hn-duration-slower) var(--hn-ease-bloom),opacity var(--hn-duration-normal)}.sun{transform:rotate(90deg) scale(0.4);opacity:0}.moon{transform:none;opacity:1}.auto{opacity:0;transform:scale(0.4)}:host([data-resolved="light"]) .sun{transform:none;opacity:1}:host([data-resolved="light"]) .moon{transform:rotate(-90deg) scale(0.4);opacity:0}:host([data-mode="auto"]) .sun,:host([data-mode="auto"]) .moon{opacity:0;transform:scale(0.4)}:host([data-mode="auto"]) .auto{opacity:1;transform:none}`;

const svg = (cls: string, body: string) =>
  `<svg class="${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

const html = `<button part="button" type="button">
  ${svg('sun', '<circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>')}
  ${svg('moon', '<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/><path d="M20 3v4"/><path d="M22 5h-4"/>')}
  ${svg('auto', '<rect width="20" height="14" x="2" y="3" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/>')}
</button>`;

/**
 * Bouton clair / sombre (hiru / yozakura), avec transition circulaire si le
 * navigateur la prend en charge. Attribut `with-auto` : ajoute le mode système.
 */
export class HnThemeToggle extends HanamiElement {
  static readonly tagName = 'hn-theme-toggle';

  private button!: HTMLButtonElement;
  private stop?: () => void;

  connectedCallback(): void {
    if (!this.shadowRoot) {
      const root = createShadow(this, css, html);
      this.button = root.querySelector('button')!;
      this.button.addEventListener('click', () => this.cycle());
    }
    this.sync();
    this.stop = onThemeChange(() => this.sync());
  }

  disconnectedCallback(): void {
    this.stop?.();
  }

  private next(): ThemeMode {
    const order: ThemeMode[] = this.hasAttribute('with-auto') ? ['light', 'dark', 'auto'] : ['light', 'dark'];
    const current = this.hasAttribute('with-auto') ? getTheme() : getResolvedTheme();
    return order[(order.indexOf(current) + 1) % order.length];
  }

  private cycle(): void {
    const rect = this.button.getBoundingClientRect();
    setTheme(this.next(), { origin: { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } });
  }

  private sync(): void {
    const mode = getTheme();
    this.dataset.mode = this.hasAttribute('with-auto') ? mode : getResolvedTheme();
    this.dataset.resolved = getResolvedTheme();
    const next = this.next();
    this.button.setAttribute('aria-label', next === 'dark' ? t('themeToDark') : next === 'light' ? t('themeToLight') : t('themeToAuto'));
    this.button.title = this.button.getAttribute('aria-label') ?? '';
  }
}

define(HnThemeToggle.tagName, HnThemeToggle);
